// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@awc/App/Store/Selectors/SettingsSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

interface ImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<ImgProps>;
}

export type ImgProps = MobiusStyledComponentProps<
    "img",
    ImagePresentationProps & {
        /** The URL to fetch the image from. If not provided, Img renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Controls whether the img is lazy or eager loaded, default is eager */
        loading?: string;
        // Event handler for screen readers accessing an image modal in ProductPrimaryImage.tsx
        onKeyPress?: React.KeyboardEventHandler<HTMLImageElement>;
        // xng-change
        showSmallImage?: boolean;
        showLargeImage?: boolean;
    }
>;

function Img(props: ImgProps) {
    // xng-change
    const { src, altText, loading, showSmallImage, showLargeImage, ...otherProps } = props;
    const [imageSrc, setImageSrc] = useState<string | undefined>(src);
    const [error, setError] = useState(false);
    const { notFoundLargeImagePath, notFoundMediumImagePath, notFoundSmallImagePath } = useAppSelector(state => ({
        notFoundLargeImagePath: (getSettingsCollection(state).catalogGeneralSettings as any)?.notFoundLargeImagePath,
        notFoundMediumImagePath: (getSettingsCollection(state).catalogGeneralSettings as any)?.notFoundMediumImagePath,
        notFoundSmallImagePath: (getSettingsCollection(state).catalogGeneralSettings as any)?.notFoundSmallImagePath,
    }));

    const dimensions = useQueryStringsDimensions(imageSrc);

    // <xng-change>
    const notFoundImage = useMemo(() => {
        let notFoundImage;

        if (showLargeImage) {
            notFoundImage = notFoundLargeImagePath;
        } else if (showSmallImage) {
            notFoundImage = notFoundSmallImagePath;
        } else {
            notFoundImage = notFoundMediumImagePath;
        }

        return notFoundImage;
    }, [showLargeImage, showSmallImage, notFoundLargeImagePath, notFoundMediumImagePath, notFoundSmallImagePath]);

    const handleNotFoundImage = ({ currentTarget }: any) => {
        setError(true);
        currentTarget.src = notFoundImage;
        setImageSrc(notFoundImage);
    };

    useEffect(() => {
        if (src) {
            setImageSrc(src);
        } else {
            setImageSrc(notFoundImage);
        }
    }, [src]);
    // </xng-change>

    if (!imageSrc && !notFoundLargeImagePath && !notFoundMediumImagePath && !notFoundSmallImagePath) {
        return null;
    }

    return (
        <StyledImg
            {...otherProps}
            loading={loading || "lazy"}
            src={imageSrc}
            alt={altText}
            width={dimensions.width || otherProps.width}
            height={dimensions.height || otherProps.height}
            onError={!error ? handleNotFoundImage : undefined}
            className={otherProps.className}
        />
    );
}

const useQueryStringsDimensions = (src?: string) => {
    return React.useMemo(() => {
        const queryStrings = src && /\?.*/.exec(src);
        const noDimensions = {
            width: "",
            height: "",
        };

        if (queryStrings) {
            const parsedUrl = parseImageUrl(src);

            if (!parsedUrl.width || !parsedUrl.height) {
                return noDimensions;
            }

            return parsedUrl;
        }
        return noDimensions;
    }, [src]);
};

const StyledImg = styled.img<InjectableCss>`
    ${injectCss}
`;

export default Img;
